import React, { FC } from "react";
import {
  OakLI,
  OakSmallSecondaryButtonWithDropdown,
  OakUiRoleToken,
} from "@oaknational/oak-components";
import { ActionsCamel } from "@oaknational/oak-curriculum-schema";

import { TeacherShareNotesButton } from "../TeacherShareNotesButton/TeacherShareNotesButton";
import { LessonOverviewHeaderShareAllButton } from "../LessonOverviewHeaderShareAllButton";

import { Breadcrumb } from "@/components/SharedComponents/Breadcrumbs";
import { LessonHeaderWrapper } from "@/components/TeacherComponents/LessonHeaderWrapper";
import { LessonOverviewHeaderMobile } from "@/components/TeacherComponents/LessonOverviewHeaderMobile";
import { LessonOverviewHeaderDesktop } from "@/components/TeacherComponents/LessonOverviewHeaderDesktop";
import { AnalyticsUseCaseValueType } from "@/browser-lib/avo/Avo";
import { TeacherNotesButtonProps } from "@/pages-helpers/teacher/useLesson/useLesson";

/**
 * This is a header for the lesson overview page.
 *
 */

export type LessonOverviewHeaderProps = {
  // pathway props
  subjectSlug: string | null;
  yearTitle?: string | null;
  examBoardTitle?: string | null;
  tierTitle?: string | null;
  unitSlug: string;
  keyStageSlug?: string | null;
  keyStageTitle?: string | null;
  unitTitle: string | null;
  subjectTitle: string | null;
  programmeSlug: string;
  // lesson base props
  lessonTitle: string;
  expired?: boolean | null;
  lessonSlug: string;
  lessonDescription?: string;
  phonicsOutcome?: string | null;
  isCanonical: boolean;
  orderInUnit?: number | null;
  unitTotalLessonCount?: number | null;
  geoRestricted?: boolean;
  loginRequired?: boolean;
  isLegacy?: boolean;
  lessonReleaseDate?: string | null;
  // other props
  breadcrumbs: Breadcrumb[];
  background: OakUiRoleToken;
  isNew: boolean;
  isShareable: boolean;
  subjectIconBackgroundColor: OakUiRoleToken;
  analyticsUseCase: AnalyticsUseCaseValueType;
  pupilLessonOutcome?: string | null;
  showDownloadAll: boolean;
  showShare: boolean;
  contentRestricted: boolean;
  // teacher share
  teacherShareButton?: React.ReactNode;
  teacherShareButtonProps?: TeacherNotesButtonProps;
  // AI
  excludedFromTeachingMaterials?: boolean;
  shouldHideCreateWithAi?: boolean;
  subjectCategories?: Array<string | number | null> | null;
  actions?: ActionsCamel | null;
};

const ShareButtons: FC<{
  contentRestricted: boolean;
  shouldUseDropdown: boolean;
  showShare: boolean;
  teacherShareButtonProps?: TeacherNotesButtonProps;
  overviewProps: LessonOverviewHeaderProps;
}> = ({
  contentRestricted,
  shouldUseDropdown,
  showShare,
  teacherShareButtonProps,
  overviewProps,
}) => {
  if (contentRestricted) {
    return null;
  }

  if (shouldUseDropdown && teacherShareButtonProps) {
    return (
      <OakLI $listStyle={"none"}>
        <OakSmallSecondaryButtonWithDropdown primaryActionText={"Share lesson"}>
          <LessonOverviewHeaderShareAllButton
            variant="dropdown"
            {...overviewProps}
          />
          <TeacherShareNotesButton
            variant="dropdown"
            {...teacherShareButtonProps}
          />
        </OakSmallSecondaryButtonWithDropdown>
      </OakLI>
    );
  }

  return (
    <>
      {showShare && (
        <OakLI $listStyle={"none"}>
          <LessonOverviewHeaderShareAllButton {...overviewProps} />
        </OakLI>
      )}
      {teacherShareButtonProps && (
        <OakLI $listStyle={"none"}>
          <TeacherShareNotesButton {...teacherShareButtonProps} />
        </OakLI>
      )}
    </>
  );
};

const LessonOverviewHeader: FC<LessonOverviewHeaderProps> = (props) => {
  const {
    breadcrumbs,
    background,
    teacherShareButtonProps,
    showShare,
    excludedFromTeachingMaterials,
    shouldHideCreateWithAi,
    contentRestricted,
  } = props;

  const shouldUseDropdown =
    !excludedFromTeachingMaterials &&
    !shouldHideCreateWithAi &&
    showShare &&
    teacherShareButtonProps;

  const shareButtons = (
    <ShareButtons
      contentRestricted={contentRestricted}
      shouldUseDropdown={!!shouldUseDropdown}
      showShare={showShare}
      teacherShareButtonProps={teacherShareButtonProps}
      overviewProps={props}
    />
  );

  return (
    <LessonHeaderWrapper breadcrumbs={breadcrumbs} background={background}>
      <LessonOverviewHeaderDesktop {...props} shareButtons={shareButtons} />
      <LessonOverviewHeaderMobile {...props} shareButtons={shareButtons} />
    </LessonHeaderWrapper>
  );
};

export default LessonOverviewHeader;
